import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image } from 'react-native';
import { CHAT_MOBILE_COLORS } from '@/constants/residents/mobile/chatColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import LinearGradient from 'react-native-linear-gradient';

const PropertyIcon = RESIDENTS_SVG_COMPONENTS.PROPERTY;
const GroupIcon = RESIDENTS_SVG_COMPONENTS.GROUP;

export interface ChatConversationCardProps {
  id: number | string;
  subject: string;
  preview: string;
  date: string;
  isGroup?: boolean;
  recipientCount?: number;
  hasUnread?: boolean;
  // Marketplace-specific props
  thumbnail?: string;
  subtitle?: string;
  onPress: (id: number | string) => void;
}

/**
 * Conversation card component for chat list
 * Displays subject, preview, date, and appropriate icon
 */
export const ChatConversationCard: React.FC<ChatConversationCardProps> = ({
  id,
  subject,
  preview,
  date,
  isGroup = false,
  recipientCount,
  hasUnread = false,
  thumbnail,
  subtitle,
  onPress,
}) => {
  const Icon = isGroup ? GroupIcon : PropertyIcon;
  const isMarketplace = !!thumbnail;

  return (
    <TouchableOpacity
      style={[styles.container, hasUnread && styles.containerUnread]}
      onPress={() => onPress(id)}
      activeOpacity={0.7}
    >
      {/* Avatar - show thumbnail image for marketplace, icon for property chat */}
      {isMarketplace ? (
        <Image
          source={{ uri: thumbnail }}
          style={styles.thumbnailAvatar}
          resizeMode="cover"
        />
      ) : (
        <View style={styles.avatar}>
          <Icon width={isGroup ? 20 : 16} height={isGroup ? 20 : 16} color={CHAT_MOBILE_COLORS.textPrimary} />
        </View>
      )}

      {/* Text content */}
      <View style={styles.content}>
        {/* Top row: Subject + Date */}
        <View style={styles.topRow}>
          <View style={styles.subjectContainer}>
            <Text style={styles.subject} numberOfLines={1}>
              {subject}
            </Text>
          </View>
          <Text style={styles.date}>{date}</Text>
        </View>

        {/* Bottom row: Preview + Unread indicator */}
        <View style={styles.bottomRow}>
          <Text style={styles.preview} numberOfLines={1}>
            {preview}
          </Text>
          {hasUnread && (
            <LinearGradient
              colors={['#F5AF19', '#F48D17', '#F36B15', '#F24913', '#F12711']}
              style={styles.unreadIndicator}
            />
          )}
        </View>

        {/* Subtitle for marketplace (seller/buyer name) or recipient count for group messages */}
        {subtitle ? (
          <Text style={styles.subtitle} numberOfLines={1}>
            {subtitle}
          </Text>
        ) : isGroup && recipientCount !== undefined && recipientCount > 0 ? (
          <Text style={styles.recipientCount}>
            {recipientCount} Recipient{recipientCount !== 1 ? 's' : ''}
          </Text>
        ) : null}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 24,
    paddingVertical: 14,
    gap: 16,
  },
  containerUnread: {
    backgroundColor: 'rgba(238, 241, 244, 0.05)',
  },
  avatar: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: CHAT_MOBILE_COLORS.avatarBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbnailAvatar: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: CHAT_MOBILE_COLORS.avatarBackground,
  },
  content: {
    flex: 1,
    gap: 4,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  subjectContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 2,
  },
  subject: {
    fontSize: 14,
    fontWeight: '600',
    color: CHAT_MOBILE_COLORS.textPrimary,
    lineHeight: 21,
    flex: 1,
  },
  date: {
    fontSize: 12,
    fontWeight: '300',
    color: CHAT_MOBILE_COLORS.textMuted,
    lineHeight: 18,
    textAlign: 'right',
  },
  bottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  preview: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    color: CHAT_MOBILE_COLORS.textSecondary,
    lineHeight: 21,
  },
  unreadIndicator: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  recipientCount: {
    fontSize: 12,
    fontWeight: '400',
    color: CHAT_MOBILE_COLORS.textMuted,
    lineHeight: 18,
  },
  subtitle: {
    fontSize: 12,
    fontWeight: '400',
    color: CHAT_MOBILE_COLORS.textMuted,
    lineHeight: 18,
  },
});

export default ChatConversationCard;
